import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Section } from 'shared/ui/Section';
import { Picker } from 'shared/ui/Picker';
import { Btn } from 'shared/ui/Btn';
import { RackThumb } from './RackThumb';
import { useRackSection } from './useRackSection';

// A smaller, local display scale than shared/lib/rackLayout's U_PX (18): up to three
// racks sit side by side inside the workbench column, and a 42U rack at 18px/U would be
// 756px tall — most of a laptop screen for a readout. Deliberately not the shared
// constant, which is the Rack View overlay's single-rack inspector scale (System Slide
// makes the same call for the same reason). 14px/U (a 168px-wide rack) is a step
// above classic's 12.25px/U: at the first cut, 11px/U, testers found it harder to
// read than classic.
const U_PX = 14;

/** The rack, always on screen. Classic draws it inside the System Attributes card and it
 *  follows the configuration as you type; V2 had it only behind the Rack View button.
 *  This is the readout — clicking a rack opens the overlay, which keeps the inspector
 *  and the add-node buttons. It sits below Directors, the last section that puts
 *  hardware in it. */
export function RackSection({ id }: { id: string }) {
  const s = useRackSection();
  return (
    <Section.Root id={id} defaultOpen>
      <Section.Bar title="Rack" role="Physical layout" summary={s.summary} />
      <Section.Body>
        <div className="flex flex-wrap items-start gap-6">
          <div className="flex gap-4">
            {s.visible.map((rack) => (
              <div key={rack.index} className="flex w-[168px] flex-col gap-1">
                <span className="text-center font-body text-xs text-ink-muted">Rack {rack.index + 1}</span>
                <RackThumb rack={rack} uPx={U_PX} framed={s.framed} vendorId={s.vendorId}
                  photographic={s.isPhotographic} onOpen={s.openRackView} testId="rack-section" />
              </div>
            ))}
          </div>

          <div className="flex min-w-[160px] flex-1 flex-col gap-4">
            <Picker
              id="rack-section-size"
              label="Rack Size"
              options={s.rackSizeOptions}
              value={s.rackSize}
              onChange={s.onRackSizeChange}
              className="w-40"
            />
            {s.paged && (
              <div role="group" aria-label="Rack pages" className="flex items-center gap-2">
                <Btn variant="outlined" size="sm" aria-label="Previous racks" disabled={!s.canPrev} onClick={s.onPrev}>
                  <ChevronLeft className="h-4 w-4" />
                </Btn>
                <span className="font-body text-sm text-ink">{s.rangeLabel}</span>
                <Btn variant="outlined" size="sm" aria-label="Next racks" disabled={!s.canNext} onClick={s.onNext}>
                  <ChevronRight className="h-4 w-4" />
                </Btn>
              </div>
            )}
            <p className="font-body text-xs text-ink-muted">Click a rack to open Rack View.</p>
          </div>
        </div>
      </Section.Body>
    </Section.Root>
  );
}
